import React from 'react';
import { Box, Button, Typography, Divider, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface Props {
  args: Record<string, any>;
  state: 'pending' | 'approved' | 'rejected';
  onResolve?: (approved: boolean) => void;
}

const Row = ({ label, value }: { label: string; value: any }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.3 }}>
      <Typography variant="caption" sx={{ minWidth: 96, color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {String(value)}
      </Typography>
    </Box>
  );
};

const formatBool = (v: any): string | null => {
  if (v === true) return 'あり';
  if (v === false) return 'なし';
  return null;
};

const formatYen = (n: any): string | null => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  return `${num.toLocaleString()} 円`;
};

export default function AgentConfirmCard({ args, state, onResolve }: Props) {
  const breakdown = args.breakdown;
  const basePrice = args.base_price ?? breakdown?.subtotal_base;
  const nocPrice = args.noc_price;
  const cdwPrice = args.cdw_price;
  const optionsPrice = args.options_price ?? breakdown?.options;
  const discount = args.discount ?? breakdown?.discount;
  const sumPrice = args.sum_price ?? breakdown?.grand_total;
  const days = args.days;
  const nights = args.nights;
  const campaign = args.campaign;
  const couponApplied = args.coupon_applied ?? breakdown?.coupon_applied;

  return (
    <Box sx={{ mx: 1.5, my: 0.8 }}>
      <Box
        sx={{
          border: '2px solid #f59e0b',
          borderRadius: 2,
          overflow: 'hidden',
          background: '#fffbeb',
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.8,
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
            color: '#fff',
          }}
        >
          <Typography variant="subtitle2" fontWeight={800}>
            📝 予約作成の確認
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Row label="予約者名" value={args.customer_name} />
          <Row label="貸出日時" value={args.pickup_datetime} />
          <Row label="返却日時" value={args.return_datetime} />
          <Row label="車両タイプID" value={args.vehicle_type_id} />
          <Row label="車両タイプ" value={args.vehicle_type_name} />
          {nights != null && <Row label="泊数" value={`${nights} 泊`} />}
          {days != null && <Row label="課金日数" value={`${days} 日`} />}
          {/* 複数台・予約経路（2026-10-19） */}
          {Number(args.quantity) > 1 && <Row label="台数" value={`${args.quantity} 台（${(args.group_codes || [])[0] ?? ''} 〜）`} />}
          <Row label="予約経路" value={args.contact_media} />

          <Divider sx={{ my: 0.8 }} />
          <Row label="基本料金" value={formatYen(basePrice)} />
          {Number(optionsPrice) > 0 && (
            <Row label="オプション" value={formatYen(optionsPrice)} />
          )}
          <Row label="NOC免責" value={args.noc_waiver === true ? `あり (${formatYen(nocPrice) ?? '—'})` : formatBool(args.noc_waiver)} />
          <Row label="CDW" value={args.cdw === true ? `あり (${formatYen(cdwPrice) ?? '—'})` : formatBool(args.cdw)} />
          {Number(discount) > 0 && (
            <Row
              label="🎉 割引"
              value={`-${formatYen(discount)}${campaign?.name ? ` (${campaign.name})` : ''}`}
            />
          )}
          {sumPrice != null && (
            <Box sx={{ display: 'flex', gap: 1, py: 0.5, mt: 0.4, borderTop: '1px solid #fed7aa' }}>
              <Typography variant="body2" sx={{ minWidth: 96, fontWeight: 800, color: '#9a3412' }}>
                {Number(args.quantity) > 1 ? '合計金額（1台目）' : '合計金額'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: '#9a3412' }}>
                {formatYen(sumPrice)}
              </Typography>
            </Box>
          )}
          {Number(args.quantity) > 1 && (
            <>
              <Row label="グループ合計" value={formatYen(args.group_total)} />
              <Row label="請求書" value={args.invoice_grouping} />
              {args.fleet_note && <Row label="フリート割" value={args.fleet_note} />}
            </>
          )}

          <Divider sx={{ my: 0.8 }} />
          <Row label="電話番号" value={args.phone} />
          <Row label="メール" value={args.email} />
          <Row label="乗車人数（大人）" value={args.pax} />
          <Row label="子供の人数" value={args.pax_children} />
          <Row label="チャイルドシート" value={args.child_seat} />
          <Row label="ブースターシート" value={args.booster_seat} />
          <Row label="到着便" value={args.arrival_flight} />
          <Row label="出発便" value={args.departure_flight} />
          <Row label="お客様の要望" value={args.comment} />

          {state === 'pending' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.2 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={() => onResolve?.(true)}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                この内容で作成する
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                onClick={() => onResolve?.(false)}
                sx={{ textTransform: 'none' }}
              >
                キャンセル
              </Button>
            </Box>
          )}
          {state === 'approved' && (
            <Chip
              icon={<CheckCircleIcon />}
              label="承認済み"
              color="success"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
          {state === 'rejected' && (
            <Chip
              icon={<CancelIcon />}
              label="キャンセル"
              color="default"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
